// Hooks' catalog and clip options (live's `hook-templates.ts`, `constants.ts` and `utils.ts`,
// spec G14-templates): the nine hooks as the picker shows them, and the length, resolution and
// ratio every picked hook's model makes. The prompts are the server's (`templates/hooks.py`).
import type { CatalogVideoModel } from "../../../api/generated/model";

/** Live's two models (`HOOK_MODEL_IDS`), by our catalog ids, which are live's. */
export const FAST = "seedance-2-fast";
export const SEEDANCE_2_5 = "seedance-2-5";
export const MODELS = [FAST, SEEDANCE_2_5] as const;

export type HookId = "01" | "02" | "03" | "04" | "05" | "06" | "07" | "08" | "09";

export interface HookModel {
  id: HookId;
  /** The model its gag is timed for: the length, resolution and ratio menus follow it. */
  model: string;
}

/**
 * Live's `HOOK_TEMPLATES`, in its order, by the model each runs on; their names, hints, posters
 * and clips are the card's (`TemplateDetail.options`).
 */
export const HOOKS: readonly HookModel[] = [
  { id: "01", model: FAST },
  { id: "02", model: FAST },
  { id: "03", model: FAST },
  { id: "04", model: FAST },
  { id: "05", model: FAST },
  { id: "06", model: FAST },
  { id: "07", model: FAST },
  { id: "08", model: SEEDANCE_2_5 },
  { id: "09", model: FAST },
];

/** A hook id the form knows, from an option's id. */
export function hookIdOf(id: string): HookId | null {
  return HOOKS.find((hook) => hook.id === id)?.id ?? null;
}

/** The hook that holds The Absurd Prop's object (`hasHeldObject`). */
export const PROP_HOOK: HookId = "05";
/** Live's Auto: each hook keeps its own length (`AUTO_DURATION`). */
export const AUTO = "auto";
export const DEFAULT_RESOLUTION = "720p";
export const DEFAULT_RATIO = "9:16";

/** The picks in live's order, whatever the order picked. */
export function inOrder(ids: readonly HookId[]): HookId[] {
  return HOOKS.map((hook) => hook.id).filter((id) => ids.includes(id));
}

export interface ClipOptions {
  /** Whole seconds every picked model makes, ascending (after live's Auto). */
  durations: number[];
  resolutions: string[];
  /** Without live's never-offered `auto`. */
  ratios: string[];
}

function shared<T>(lists: readonly (readonly T[])[]): T[] {
  const [first = [], ...others] = lists;
  return first.filter((each) => others.every((other) => other.includes(each)));
}

/**
 * Live's `getClipSettingOptions`: what every picked hook's model makes (its reference to video
 * block's, else the row's), or both models' while none is picked.
 */
export function clipOptions(models: readonly CatalogVideoModel[], picked: readonly HookId[]): ClipOptions {
  const ids = picked.length === 0 ? [...MODELS] : [...new Set(picked.map((id) => modelOf(id)))];
  const rows = ids.flatMap((id) => models.filter((model) => model.id === id));
  const blocks = rows.map((row) => ({ row, block: row.modes["video.reference"] }));
  const durations = shared(blocks.map(({ row, block }) => (block?.durations ?? row.durations)?.values ?? []));
  return {
    durations: [...durations].sort((a, b) => a - b),
    resolutions: shared(blocks.map(({ row, block }) => (block?.resolutions ?? row.resolutions)?.values ?? [])),
    ratios: shared(blocks.map(({ row, block }) => (block?.aspect_ratios ?? row.aspect_ratios).values)).filter(
      (ratio) => ratio !== AUTO,
    ),
  };
}

function modelOf(id: HookId): string {
  return HOOKS.find((hook) => hook.id === id)?.model ?? FAST;
}

export interface ClipSettings {
  duration: string;
  resolution: string;
  ratio: string;
}

/**
 * Live's `normalizeClipSettings` after Apply: a length no longer offered goes back to Auto, a
 * resolution or ratio to live's default where offered, else the first offered.
 */
export function normalized(settings: ClipSettings, options: ClipOptions): ClipSettings {
  const fallback = (value: string, offered: string[], preferred: string) =>
    offered.includes(value) ? value : offered.includes(preferred) ? preferred : (offered[0] ?? value);
  return {
    duration: settings.duration === AUTO || options.durations.map(String).includes(settings.duration) ? settings.duration : AUTO,
    resolution: fallback(settings.resolution, options.resolutions, DEFAULT_RESOLUTION),
    ratio: fallback(settings.ratio, options.ratios, DEFAULT_RATIO),
  };
}
